import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from 'react-i18next';
import { getTranslatedActivity } from '../translations/activityTranslations';
import { FaEye } from 'react-icons/fa';

function ActivitiesNew() {
    const { i18n } = useTranslation();
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const querySnapshot = await getDocs(collection(db, "activities"));
            const data = querySnapshot.docs.map((doc) => {
                const docData = doc.data();

                // Handle location - ensure it's always a string
                const getLocationString = (loc) => {
                    if (!loc) return '';
                    if (typeof loc === 'string') return loc;
                    if (typeof loc === 'object' && loc._lat !== undefined && loc._long !== undefined) {
                        return `${loc._lat}, ${loc._long}`;
                    }
                    return '';
                };

                return {
                    id: doc.id,
                    name: docData.name || '',
                    nameEn: docData.nameEn || '',
                    description: docData.description || '',
                    descriptionEn: docData.descriptionEn || '',
                    date: docData.date || '',
                    location: getLocationString(docData.location),
                    locationEn: getLocationString(docData.locationEn),
                    imgURL: docData.imgURL || '',
                    category: docData.category || 'ประเภทกิจกรรม',
                    latitude: (docData.latitude?._lat !== undefined) ? docData.latitude._lat : (typeof docData.latitude === 'number' ? docData.latitude : 0),
                    longitude: (docData.longitude?._long !== undefined) ? docData.longitude._long : (typeof docData.longitude === 'number' ? docData.longitude : 0)
                };
            });
            setActivities(data);
            setLoading(false);
        };
        fetchData();
    }, []);

    const parseDate = (date) => {
        if (!date) return i18n.language === 'en' ? "Unknown date" : "ไม่ทราบวันที่";
        if (date && date.seconds) {
            return new Date(date.seconds * 1000).toLocaleDateString();
        }
        if (typeof date === 'string') {
            const parsedDate = new Date(date);
            return isNaN(parsedDate) ? (i18n.language === 'en' ? "Unknown date" : "ไม่ทราบวันที่") : parsedDate.toLocaleDateString();
        }
        return i18n.language === 'en' ? "Unknown date" : "ไม่ทราบวันที่";
    };

    const handleActivityClick = (activity) => {
        navigate(`/activity/${activity.name}`, { state: { activities: [activity] } });
    };

    const filteredActivities = activities.filter(activity => {
        const translated = getTranslatedActivity(activity, i18n.language);
        const matchesSearch = searchTerm === '' || 
            translated.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            translated.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            translated.location.toLowerCase().includes(searchTerm.toLowerCase());
        
        return matchesSearch;
    });

    return (
        <div style={{ paddingTop: '80px', minHeight: '100vh', background: 'var(--gray-50)' }}>
            {/* Hero Section */}
            <section style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
                padding: '4rem 0 3rem',
                marginBottom: '3rem'
            }}>
                <div className="container-modern text-center">
                    <motion.h1
                        style={{
                            fontSize: '3rem',
                            fontWeight: '800',
                            color: 'white',
                            marginBottom: '1rem'
                        }}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        {i18n.language === 'en' ? 'Our Activities Gallery' : 'แกลเลอรีกิจกรรม'}
                    </motion.h1>
                    <motion.p
                        style={{
                            fontSize: '1.25rem',
                            color: 'rgba(255, 255, 255, 0.9)',
                            maxWidth: '600px',
                            margin: '0 auto'
                        }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        {i18n.language === 'en'
                            ? 'Explore all the wonderful activities we have done'
                            : 'สำรวจกิจกรรมที่ยอดเยี่ยมทั้งหมดที่เราได้ทำมา'}
                    </motion.p>
                </div>
            </section>

            {/* Search Bar */}
            <div className="container-modern mb-4">
                <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                    <div style={{
                        position: 'relative',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        borderRadius: '12px',
                        overflow: 'hidden'
                    }}>
                        <input
                            type="text"
                            placeholder={i18n.language === 'en' ? '🔍 Search activities by name, description, or location...' : '🔍 ค้นหากิจกรรมจากชื่อ คำอธิบาย หรือสถานที่...'}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '1rem 1.25rem',
                                fontSize: '1rem',
                                border: '2px solid var(--blue-100)',
                                borderRadius: '12px',
                                outline: 'none',
                                transition: 'all 0.3s ease',
                                backgroundColor: 'white'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = 'var(--blue-500)';
                                e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = 'var(--blue-100)';
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                style={{
                                    position: 'absolute',
                                    right: '1rem',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'var(--gray-200)',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: 'var(--gray-600)',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = 'var(--gray-300)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'var(--gray-200)';
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                    {searchTerm && (
                        <p style={{
                            textAlign: 'center',
                            marginTop: '0.75rem',
                            color: 'var(--gray-600)',
                            fontSize: '0.9rem'
                        }}>
                            {i18n.language === 'en'
                                ? `Found ${filteredActivities.length} result${filteredActivities.length !== 1 ? 's' : ''}`
                                : `พบ ${filteredActivities.length} กิจกรรม`}
                        </p>
                    )}
                </div>
            </div>

            {/* Activities Table/List */}
            <div className="container-modern" style={{ paddingBottom: '4rem' }}>
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3" style={{ color: 'var(--gray-600)' }}>
                            {i18n.language === 'en' ? 'Loading activities...' : 'กำลังโหลดกิจกรรม...'}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="mb-4" style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0 1rem'
                        }}>
                            <p style={{ color: 'var(--gray-600)', margin: 0 }}>
                                {i18n.language === 'en'
                                    ? `Showing ${filteredActivities.length} activities`
                                    : `แสดง ${filteredActivities.length} กิจกรรม`
                                }
                            </p>
                        </div>

                        {/* Table/List Container */}
                        <div className="activities-table-container">
                            {/* Table Header - Desktop Only */}
                            <div className="activities-table-header">
                                <div>{i18n.language === 'en' ? 'Image' : 'รูปภาพ'}</div>
                                <div>{i18n.language === 'en' ? 'Activity Name' : 'ชื่อกิจกรรม'}</div>
                                <div>{i18n.language === 'en' ? 'Date' : 'วันที่'}</div>
                                <div>{i18n.language === 'en' ? 'Location' : 'สถานที่'}</div>
                                <div>{i18n.language === 'en' ? 'Status' : 'สถานะ'}</div>
                                <div style={{ textAlign: 'center' }}>{i18n.language === 'en' ? 'Actions' : 'จัดการ'}</div>
                            </div>

                            {/* Table Body */}
                            {filteredActivities.map((activity, index) => {
                                const translated = getTranslatedActivity(activity, i18n.language);
                                return (
                                    <motion.div
                                        key={activity.id}
                                        className="activity-row"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3, delay: index * 0.05 }}
                                        whileHover={{ background: 'var(--gray-50)' }}
                                        onClick={() => handleActivityClick(activity)}
                                    >
                                        {/* Thumbnail */}
                                        <div className="activity-thumbnail">
                                            {activity.imgURL ? (
                                                <img
                                                    src={activity.imgURL}
                                                    alt={translated.name}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    fontSize: '0.7rem',
                                                    fontWeight: '600'
                                                }}>
                                                    {i18n.language === 'en' ? 'No Image' : 'ไม่มีรูป'}
                                                </div>
                                            )}
                                        </div>

                                        {/* Activity Name & Description */}
                                        <div className="activity-content">
                                            <h3 className="activity-title">
                                                {translated.name}
                                            </h3>
                                            <p className="activity-description">
                                                {translated.description}
                                            </p>
                                        </div>

                                        {/* Date */}
                                        <div className="activity-date">
                                            <span className="activity-label">{i18n.language === 'en' ? 'Date:' : 'วันที่:'}</span>
                                            {parseDate(activity.date)}
                                        </div>

                                        {/* Location */}
                                        <div className="activity-location">
                                            <span className="activity-label">{i18n.language === 'en' ? 'Location:' : 'สถานที่:'}</span>
                                            {translated.location || (i18n.language === 'en' ? 'N/A' : 'ไม่ระบุ')}
                                        </div>

                                        {/* Status (Category) */}
                                        <div className="activity-status">
                                            <span className="activity-badge">
                                                <span className="badge-dot"></span>
                                                {activity.category}
                                            </span>
                                        </div>

                                        {/* Actions */}
                                        <div className="activity-actions">
                                            <motion.button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleActivityClick(activity);
                                                }}
                                                className="view-btn"
                                                whileHover={{ scale: 1.05, background: '#7c3aed' }}
                                                whileTap={{ scale: 0.95 }}
                                            >
                                                <FaEye size={14} />
                                                {i18n.language === 'en' ? 'View' : 'ดู'}
                                            </motion.button>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {filteredActivities.length === 0 && (
                            <div className="text-center py-5">
                                <p style={{ fontSize: '1.2rem', color: 'var(--gray-600)' }}>
                                    {i18n.language === 'en'
                                        ? 'No activities found in this category'
                                        : 'ไม่พบกิจกรรมในหมวดหมู่นี้'}
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default ActivitiesNew;
